import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Shield, 
  CheckCircle,
  ArrowRight,
  CheckCircle2
} from 'lucide-react'
import { services } from '../lib/data'

const SecurityPage = () => {
  const securityServices = services.filter(s => s.category === 'security')

  const methodology = [
    {
      phase: 'Discovery & Reconnaissance',
      description: 'Comprehensive asset discovery and information gathering',
      tools: ['Nmap', 'Shodan', 'OSINT Tools', 'Custom Scripts']
    },
    {
      phase: 'Vulnerability Assessment',
      description: 'Automated and manual vulnerability identification',
      tools: ['Nuclei', 'OWASP ZAP', 'Nessus', 'Custom Scanners']
    },
    {
      phase: 'Penetration Testing',
      description: 'Exploitation and proof-of-concept validation',
      tools: ['Burp Suite', 'Metasploit', 'Custom Exploits', 'Social Engineering']
    },
    {
      phase: 'Reporting & Remediation',
      description: 'Detailed findings and actionable recommendations',
      tools: ['Custom Templates', 'Risk Scoring', 'Remediation Guides']
    }
  ]

  const complianceFrameworks = [
    { name: 'OWASP Top 10', description: 'Web application security standards' },
    { name: 'NIST Cybersecurity Framework', description: 'Comprehensive security framework' },
    { name: 'ISO 27001', description: 'Information security management' },
    { name: 'SOC 2', description: 'Security, availability, and confidentiality' },
    { name: 'PCI DSS', description: 'Payment card industry security' },
    { name: 'HIPAA', description: 'Healthcare data protection' }
  ]

  return (
    <>
      <Helmet>
        <title>Security Services - DevSecRoot</title>
        <meta name="description" content="Comprehensive security services including penetration testing, vulnerability assessment, compliance consulting, and incident response." />
        <link rel="canonical" href="https://devsecroot.com/security" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-dsr-text mb-6">
              Security Services
            </h1>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Comprehensive security solutions to protect your applications, infrastructure, 
              and data from modern threats. From penetration testing to compliance consulting.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Our Security Services
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              End-to-end security solutions designed to identify vulnerabilities, 
              assess risks, and implement robust security controls.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover p-6 h-full flex flex-col"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                
                <h3 className="text-xl font-semibold text-dsr-text mb-3">{service.title}</h3>
                <p className="text-dsr-text/70 mb-4 flex-grow">{service.description}</p>
                
                <div className="space-y-2 mb-6">
                  {service.features.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-dsr-accent flex-shrink-0" />
                      <span className="text-sm text-dsr-text/80">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Link 
                  to={service.link}
                  className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors mt-auto"
                >
                  <span>Learn more</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Our Security Methodology
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              A proven, systematic approach to security assessment and testing
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {methodology.map((step, index) => (
              <motion.div
                key={step.phase}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover p-6 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-black">{index + 1}</span>
                </div>
                
                <h3 className="text-lg font-semibold text-dsr-text mb-3">{step.phase}</h3>
                <p className="text-dsr-text/70 mb-4">{step.description}</p>
                
                <div className="space-y-2">
                  {step.tools.map((tool, idx) => (
                    <div key={idx} className="text-sm text-dsr-accent bg-dsr-accent/10 px-3 py-1 rounded-lg">
                      {tool}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
                Compliance & Standards
              </h2>
              <p className="text-lg text-dsr-text/70 mb-6">
                We help organizations achieve and maintain compliance with industry standards 
                and regulatory requirements. Our expertise spans multiple frameworks and industries.
              </p>
              <p className="text-lg text-dsr-text/70 mb-6">
                From initial assessment to ongoing monitoring, we provide comprehensive 
                compliance support to ensure your organization meets all requirements.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 gap-4"
            >
              {complianceFrameworks.map((framework, index) => (
                <motion.div
                  key={framework.name}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="glass-card-hover p-4"
                >
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-dsr-accent flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-dsr-text">{framework.name}</h4>
                      <p className="text-sm text-dsr-text/70">{framework.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Ready to Secure Your Applications?
            </h2>
            <p className="text-xl text-dsr-text/70 mb-8 max-w-2xl mx-auto">
              Let's discuss your security needs and create a comprehensive plan to protect your assets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Book Security Assessment
              </Link>
              <Link to="/case-studies" className="btn-secondary text-lg px-8 py-4">
                View Security Case Studies
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default SecurityPage
